import React, { ReactElement } from 'react';
import classNames from 'classnames';
import { compose } from 'redux';
import { ProductsAnalyticsProvider } from 'b2b-apps/core/analytics/context/products/ProductsAnalyticsContext';
import { usePageViewAnalytics } from 'b2b-apps/shop/analytics/hooks';
import { EVENT_SOURCE, TEALIUM_PAGE_TYPE } from 'b2b-apps/shop/analytics/types/types';
import { Page } from 'b2b-apps/shop/app/components/App/Page';
import { withAllFailHandler } from 'b2b-apps/shop/common/hoc/withAllFailHandler';
import { withLoader } from 'b2b-apps/shop/common/hoc/withLoader';
import { ContentBlock } from 'b2b-apps/shop/content/components/ContentBlock';
import { Full } from 'b2b-apps/shop/content/components/ModularPage/ModularPage/Layouts/Full';
import { Sidebar } from 'b2b-apps/shop/content/components/ModularPage/ModularPage/Layouts/Sidebar';
import { getContentPageType } from 'b2b-apps/shop/content/utils/contentSelectors';
import { CMS_NEWSROOM_PAGE_ID } from 'b2b-common/core/content/newsroom/utils';
import { ComponentType, Props } from './';

const ModularPage: ComponentType = ({
  content,
  headerComponents = [],
  newsroomHeader = [],
  newsroomFooter = [],
  newsroomMain = [],
  sidebarComponents = [],
  headData,
  pageTitle,
  entryId,
}: Props): ReactElement | null => {
  usePageViewAnalytics({
    pageType: entryId ? TEALIUM_PAGE_TYPE.staticPage : TEALIUM_PAGE_TYPE.homepage,
    remoteRecord: content,
    data: {
      page: {
        id: `${entryId || ''}`,
      },
      contentPageType: getContentPageType(content),
    },
  });

  if (!content.data) {
    return null;
  }
  const { data } = content;

  const isSingleNewsroomPage = content.data.contentTypeId === CMS_NEWSROOM_PAGE_ID.singleNews;
  const isCustomDownloadPage = content.data.contentTypeId === CMS_NEWSROOM_PAGE_ID.downloadPage;

  const rootClassName = classNames(
    'cds-cms-Container',
    'cds-cms-ModularPage',
    'cds-cms-TextMediaContainer',
    'Content--CMS',
  );

  const isTitleHidden = data?.hide_title ? !!parseInt(data.hide_title, 10)
    : isSingleNewsroomPage || isCustomDownloadPage;
  const mptId = data.global__helper_mpt_id?.[0]?.mpt_id;

  return (
    <ProductsAnalyticsProvider eventSource={EVENT_SOURCE.cms}>
      <Page
        title={pageTitle}
        metaTags={headData.metaTags}
        mptId={mptId}
        className={rootClassName}
      >
        {!isTitleHidden && <h1 className='PageTitle'>{data.title}</h1>}
        {sidebarComponents.length > 0 && (
          <Sidebar header={headerComponents} sidebar={sidebarComponents}>
            {newsroomHeader.length > 0 && newsroomHeader}
            <ContentBlock content={content} />
            {newsroomMain.length > 0 && newsroomMain}
            {newsroomFooter.length > 0 && newsroomFooter}
          </Sidebar>
        )}
        {sidebarComponents.length === 0 && (
          <Full className='cds-cms-LayoutBlock--Full' header={headerComponents}>
            {newsroomHeader.length > 0 && newsroomHeader}
            <ContentBlock content={content} />
            {newsroomMain.length > 0 && newsroomMain}
            {newsroomFooter.length > 0 && newsroomFooter}
          </Full>
        )}
      </Page>
    </ProductsAnalyticsProvider>
  );
};

const ComposedModularPage = compose(
  withLoader((props: Props) => [props.content], true),
  withAllFailHandler((props: Props) => [props.content]),
)(ModularPage);

export { ComposedModularPage as ModularPage };
